import { useTranslation } from "react-i18next";
import { useParticipantSearch, useUsersCache } from "../../hooks";
import { useMemo } from "react";
import { useFieldContext } from "../../form-context";
import type { BulkParticipant } from "@pulse/api/participantApi";
import { ParticipantSelectorExternal } from "./ParticipantSelectorExternal";
import { useCurrentCustomerId } from "@pulse/hooks/useCustomerId";
import VirtualizedUsersList from "./VirtualizedUsersList";

export default function ParticipantSelector() {
    const { t } = useTranslation();
    const { search, debouncedSearch } = useParticipantSearch();
    const field = useFieldContext<BulkParticipant[]>();
    const participants = useMemo(() => field.state.value || [], [field.state.value]);
    const customerId = useCurrentCustomerId();

    const { users } = useUsersCache(debouncedSearch, customerId);

    const showExternal = useMemo(() => {
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(search?.trim() || "");
        const isNotAlreadySelected = !participants.find(
            (p) => p.isExternal && p.email === search.trim(),
        );
        const isNotUser = !users.data.find(
            (u) => u.email.toLowerCase() === search.trim().toLowerCase(),
        );

        return isValidEmail && isNotAlreadySelected && isNotUser;
    }, [search, participants, users.data]);

    return (
        <div className="mt-4 border border-tertiary shadow-md border-b-4 rounded-md">
            <div className="flex items-center justify-between p-4">
                <h3>{t("userSelector.yourPeople")}</h3>
                <p className="text-sm text-primary/50">
                    {t("userSelector.results", { count: users?.total || 0 })}
                </p>
            </div>
            <div className="text-sm text-on-container/70">
                {showExternal && <ParticipantSelectorExternal email={search.trim()} />}

                <VirtualizedUsersList />
            </div>
        </div>
    );
}
